import { useState } from 'react';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { Calendar } from '@/components/ui/calendar';
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from '@/components/ui/popover';
import { CalendarIcon, Loader2 } from 'lucide-react';
import { format } from 'date-fns';
import { cn } from '@/lib/utils';
import { toast } from 'sonner';

interface CompleteActionDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  currentAction: {
    action: string;
    dueDate: string;
  } | null;
  caseId: string;
  onComplete: (data: CompleteActionData) => void;
}

export interface CompleteActionData {
  outcome: string;
  notes: string;
  nextAction?: string;
  nextActionDate?: Date;
  newStatus?: string;
}

export function CompleteActionDialog({
  open,
  onOpenChange,
  currentAction,
  caseId,
  onComplete,
}: CompleteActionDialogProps) {
  console.log("CompleteActionDialog rendered - open:", open, "currentAction:", currentAction);
  
  const [outcome, setOutcome] = useState('');
  const [notes, setNotes] = useState('');
  const [nextAction, setNextAction] = useState('');
  const [nextActionDate, setNextActionDate] = useState<Date>();
  const [newStatus, setNewStatus] = useState('');
  const [isSubmitting, setIsSubmitting] = useState(false);

  // Reset form when dialog opens/closes
  const handleOpenChange = (newOpen: boolean) => {
    if (!newOpen) {
      setOutcome('');
      setNotes('');
      setNextAction('');
      setNextActionDate(undefined);
      setNewStatus('');
    }
    onOpenChange(newOpen);
  };

  // Get outcome options based on current action
  const getOutcomeOptions = (action: string) => {
    const outcomeMap: Record<string, string[]> = {
      'Send payment reminder email': [
        'Email sent successfully',
        'Email bounced - invalid address',
        'Customer acknowledged receipt',
        'No response received',
      ],
      'Schedule follow-up call': [
        'Call scheduled successfully',
        'Customer requested callback',
        'Unable to reach customer',
        'Left voicemail',
      ],
      'Escalate to manager': [
        'Escalated to collections manager',
        'Escalated to legal team',
        'Escalated to senior collector',
      ],
      'Send final notice': [
        'Final notice sent',
        'Customer acknowledged',
        'Delivery failed',
        'Customer disputed',
      ],
      'Arrange payment plan': [
        'Payment plan agreed',
        'Payment plan proposal sent',
        'Customer declined payment plan',
        'Negotiating payment terms',
      ],
      'Request updated contact info': [
        'Contact information updated',
        'Customer provided new details',
        'Unable to verify information',
        'Awaiting customer response',
      ],
      'Send account statement': [
        'Statement sent successfully',
        'Customer confirmed receipt',
        'Delivery failed',
        'Customer disputed balance',
      ],
      'Schedule payment discussion': [
        'Discussion scheduled',
        'Customer committed to payment',
        'Customer requested extension',
        'Unable to reach agreement',
      ],
    };

    return outcomeMap[action] || [
      'Completed successfully',
      'Partially completed',
      'Unable to complete',
      'Requires follow-up',
    ];
  };

  // Next action options
  const nextActionOptions = [
    { value: 'Send payment reminder email', label: 'Send payment reminder email' },
    { value: 'Schedule follow-up call', label: 'Schedule follow-up call' },
    { value: 'Escalate to manager', label: 'Escalate to manager' },
    { value: 'Send final notice', label: 'Send final notice' },
    { value: 'Arrange payment plan', label: 'Arrange payment plan' },
    { value: 'Request updated contact info', label: 'Request updated contact info' },
    { value: 'Send account statement', label: 'Send account statement' },
    { value: 'Schedule payment discussion', label: 'Schedule payment discussion' },
    { value: 'separator-1', label: '─────────────────────', disabled: true },
    { value: 'Close case - Payment received', label: 'Close case - Payment received' },
    { value: 'Close case - Written off', label: 'Close case - Written off' },
    { value: 'separator-2', label: '─────────────────────', disabled: true },
    { value: 'Put on hold - Customer dispute', label: 'Put on hold - Customer dispute' },
    { value: 'Put on hold - Awaiting information', label: 'Put on hold - Awaiting information' },
  ];

  // Check if selected next action requires status change
  const getStatusChangeInfo = (action: string) => {
    // Ignore separator values
    if (action.startsWith('separator-')) {
      return { requiresStatusChange: false };
    }
    if (action.startsWith('Close case')) {
      return {
        requiresStatusChange: true,
        newStatus: 'Closed',
        statusOptions: [{ value: 'Closed', label: 'Closed' }],
      };
    }
    if (action.startsWith('Put on hold')) {
      return {
        requiresStatusChange: true,
        newStatus: 'On Hold',
        statusOptions: [{ value: 'On Hold', label: 'On Hold' }],
      };
    }
    return { requiresStatusChange: false };
  };

  const statusChangeInfo = nextAction ? getStatusChangeInfo(nextAction) : { requiresStatusChange: false };

  // Handle form submission
  const handleSubmit = async () => {
    // Validation
    if (!outcome) {
      toast.error('Please select an outcome');
      return;
    }
    if (!notes || notes.trim().length < 10) {
      toast.error('Please provide detailed notes (at least 10 characters)');
      return;
    }
    // Check if separator was selected (shouldn't happen with disabled, but just in case)
    if (nextAction && nextAction.startsWith('separator-')) {
      toast.error('Please select a valid next action');
      return;
    }
    if (nextAction && !nextActionDate && !statusChangeInfo.requiresStatusChange) {
      toast.error('Please select a date for the next action');
      return;
    }
    if (statusChangeInfo.requiresStatusChange && !newStatus) {
      toast.error('Please select the new status');
      return;
    }

    setIsSubmitting(true);

    try {
      // Simulate API call
      await new Promise((resolve) => setTimeout(resolve, 1000));

      const data: CompleteActionData = {
        outcome,
        notes: notes.trim(),
        nextAction: nextAction && !nextAction.startsWith('separator-') ? nextAction : undefined,
        nextActionDate: nextActionDate,
        newStatus: newStatus || undefined,
      };

      onComplete(data);

      toast.success('Action completed successfully', {
        description: nextAction
          ? `Next action: ${nextAction}`
          : 'No next action scheduled',
      });

      handleOpenChange(false);
    } catch {
      toast.error('Failed to complete action', {
        description: 'Please try again or contact support',
      });
    } finally {
      setIsSubmitting(false);
    }
  };

  if (!currentAction) return null;

  const outcomeOptions = getOutcomeOptions(currentAction.action);

  return (
    <Dialog open={open} onOpenChange={handleOpenChange}>
      <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle>Complete Action</DialogTitle>
          <DialogDescription>
            Complete the current action for case {caseId} and set the next steps
          </DialogDescription>
        </DialogHeader>

        <div className="space-y-6 py-4">
          {/* Current Action Info */}
          <div className="rounded-lg border bg-muted/50 p-4">
            <h4 className="text-sm font-medium mb-2">Current Action</h4>
            <p className="text-sm">{currentAction.action}</p>
            <p className="text-xs text-muted-foreground mt-1">
              Due: {format(new Date(currentAction.dueDate), 'PPP')}
            </p>
          </div>

          {/* Outcome Selection */}
          <div className="space-y-2">
            <Label htmlFor="outcome">
              Outcome <span className="text-destructive">*</span>
            </Label>
            <Select value={outcome} onValueChange={setOutcome}>
              <SelectTrigger id="outcome">
                <SelectValue placeholder="Select the outcome of this action..." />
              </SelectTrigger>
              <SelectContent>
                {outcomeOptions.map((option) => (
                  <SelectItem key={option} value={option}>
                    {option}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>

          {/* Notes */}
          <div className="space-y-2">
            <Label htmlFor="notes">
              Notes <span className="text-destructive">*</span>
            </Label>
            <Textarea
              id="notes"
              placeholder="Provide detailed notes about what happened..."
              value={notes}
              onChange={(e) => setNotes(e.target.value)}
              rows={4}
              className="resize-none"
            />
            <p className="text-xs text-muted-foreground">
              These notes will be added to the case history
            </p>
          </div>

          {/* Next Action Selection */}
          <div className="space-y-2">
            <Label htmlFor="next-action">Next Action</Label>
            <Select value={nextAction} onValueChange={setNextAction}>
              <SelectTrigger id="next-action">
                <SelectValue placeholder="Select the next action to take..." />
              </SelectTrigger>
              <SelectContent>
                {nextActionOptions.map((option) => (
                  <SelectItem
                    key={option.value}
                    value={option.value}
                    disabled={option.disabled}
                  >
                    {option.label}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>

          {/* Next Action Date (shown only if next action is selected) */}
          {nextAction && !statusChangeInfo.requiresStatusChange && (
            <div className="space-y-2">
              <Label htmlFor="next-action-date">
                Next Action Date <span className="text-destructive">*</span>
              </Label>
              <Popover>
                <PopoverTrigger asChild>
                  <Button
                    id="next-action-date"
                    variant="outline"
                    className={cn(
                      'w-full justify-start text-left font-normal',
                      !nextActionDate && 'text-muted-foreground'
                    )}
                  >
                    <CalendarIcon className="mr-2 h-4 w-4" />
                    {nextActionDate ? (
                      format(nextActionDate, 'PPP')
                    ) : (
                      <span>Pick a date</span>
                    )}
                  </Button>
                </PopoverTrigger>
                <PopoverContent className="w-auto p-0" align="start">
                  <Calendar
                    mode="single"
                    selected={nextActionDate}
                    onSelect={setNextActionDate}
                    disabled={(date) => date < new Date(new Date().setHours(0, 0, 0, 0))}
                    initialFocus
                  />
                </PopoverContent>
              </Popover>
            </div>
          )}

          {/* Status Change (shown only if next action requires it) */}
          {statusChangeInfo.requiresStatusChange && (
            <div className="rounded-lg border border-amber-200 bg-amber-50 p-4 space-y-3">
              <div className="flex items-start gap-2">
                <div className="text-amber-600 mt-0.5">⚠️</div>
                <div className="flex-1">
                  <h4 className="text-sm font-medium text-amber-900">
                    Status Change
                  </h4>
                  <p className="text-xs text-amber-700 mt-1">
                    This action will change the case status
                  </p>
                </div>
              </div>
              <div className="space-y-2">
                <Label htmlFor="new-status">
                  New Status <span className="text-destructive">*</span>
                </Label>
                <Select
                  value={newStatus}
                  onValueChange={setNewStatus}
                  defaultValue={statusChangeInfo.newStatus}
                >
                  <SelectTrigger id="new-status">
                    <SelectValue placeholder="Select new status..." />
                  </SelectTrigger>
                  <SelectContent>
                    {statusChangeInfo.statusOptions?.map((option) => (
                      <SelectItem key={option.value} value={option.value}>
                        {option.label}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
            </div>
          )}
        </div>

        <DialogFooter>
          <Button
            variant="outline"
            onClick={() => handleOpenChange(false)}
            disabled={isSubmitting}
          >
            Cancel
          </Button>
          <Button onClick={handleSubmit} disabled={isSubmitting}>
            {isSubmitting ? (
              <>
                <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                Completing...
              </>
            ) : (
              'Complete Action'
            )}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
